import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { CalendarClock, Plus, User, Mail, Briefcase, Clock, Video, MapPin, Phone, CheckCircle2, XCircle, MoreHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  in_person: { label: 'In Person', icon: MapPin, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  video: { label: 'Video Call', icon: Video, color: 'text-[#00A6A0]', bg: 'bg-[#00A6A0]/10' },
  phone: { label: 'Phone', icon: Phone, color: 'text-amber-500', bg: 'bg-amber-500/10' },
};

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'text-blue-400 bg-blue-500/10' },
  completed: { label: 'Completed', color: 'text-emerald-400 bg-emerald-500/10' },
  cancelled: { label: 'Cancelled', color: 'text-red-400 bg-red-500/10' },
  no_show: { label: 'No Show', color: 'text-amber-400 bg-amber-500/10' },
};

export default function InterviewScheduling() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showStatus, setShowStatus] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    candidate_name: '', candidate_email: '', position: '',
    interview_type: 'in_person', scheduled_date: '', scheduled_time: '',
    duration_minutes: 30, notes: ''
  });
  const [statusForm, setStatusForm] = useState({ status: 'completed', feedback: '' });

  useEffect(() => { fetchInterviews(); }, []);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/interviews`, { withCredentials: true });
      setInterviews(data);
    } catch { toast.error('Failed to load interviews'); }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.candidate_name || !form.scheduled_date || !form.scheduled_time) {
      toast.error('Please fill required fields');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/interviews`, form, { withCredentials: true });
      toast.success('Interview scheduled');
      setShowCreate(false);
      setForm({ candidate_name: '', candidate_email: '', position: '', interview_type: 'in_person', scheduled_date: '', scheduled_time: '', duration_minutes: 30, notes: '' });
      fetchInterviews();
    } catch { toast.error('Failed to schedule interview'); }
    setCreating(false);
  };

  const updateStatus = async (interviewId) => {
    try {
      await axios.put(`${API_URL}/api/interviews/${interviewId}/status`, statusForm, { withCredentials: true });
      toast.success('Interview updated');
      setShowStatus(null);
      fetchInterviews();
    } catch { toast.error('Failed to update interview'); }
  };

  const scheduled = interviews.filter(i => i.status === 'scheduled');
  const past = interviews.filter(i => i.status !== 'scheduled');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="interview-scheduling-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Interview Scheduling</h1>
            <p className="text-muted-foreground mt-1">{interviews.length} interview(s) total</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" data-testid="schedule-interview-btn">
                <Plus className="w-4 h-4 mr-2" /> Schedule Interview
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg glass-card border-border">
              <DialogHeader>
                <DialogTitle className="text-foreground">Schedule New Interview</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Candidate Name *</Label>
                    <Input value={form.candidate_name} onChange={e => setForm(f => ({ ...f, candidate_name: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="interview-candidate-name" />
                  </div>
                  <div>
                    <Label className="text-foreground">Email</Label>
                    <Input type="email" value={form.candidate_email} onChange={e => setForm(f => ({ ...f, candidate_email: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="interview-candidate-email" />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">Position</Label>
                  <Input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="e.g. Senior Caregiver" className="mt-1 bg-muted border-border text-foreground" data-testid="interview-position" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Date *</Label>
                    <Input type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="interview-date" />
                  </div>
                  <div>
                    <Label className="text-foreground">Time *</Label>
                    <Input type="time" value={form.scheduled_time} onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="interview-time" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Type</Label>
                    <Select value={form.interview_type} onValueChange={v => setForm(f => ({ ...f, interview_type: v }))}>
                      <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="interview-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_person">In Person</SelectItem>
                        <SelectItem value="video">Video Call</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-foreground">Duration (min)</Label>
                    <Input type="number" value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 30 }))} className="mt-1 bg-muted border-border text-foreground" data-testid="interview-duration" />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">Notes</Label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full rounded-md bg-muted border border-border text-foreground p-3 text-sm min-h-[80px] resize-none" placeholder="Any notes for this interview..." data-testid="interview-notes" />
                </div>
                <Button type="submit" className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" disabled={creating} data-testid="create-interview-btn">
                  {creating ? 'Scheduling...' : 'Schedule Interview'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : interviews.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-xl border border-border">
            <CalendarClock className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground text-lg">No interviews scheduled</p>
            <p className="text-sm text-muted-foreground mt-1">Schedule your first candidate interview</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Upcoming */}
            {scheduled.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-foreground mb-3">Upcoming ({scheduled.length})</h2>
                <div className="space-y-3">
                  {scheduled.map((iv, i) => {
                    const typeConf = TYPE_CONFIG[iv.interview_type] || TYPE_CONFIG.in_person;
                    const TypeIcon = typeConf.icon;
                    return (
                      <Card key={i} className="glass-card border-border" data-testid={`interview-upcoming-${i}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className={`p-2.5 rounded-xl ${typeConf.bg}`}>
                              <TypeIcon className={`w-5 h-5 ${typeConf.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="text-foreground font-medium">{iv.candidate_name}</p>
                                <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[iv.status]?.color}`}>
                                  {STATUS_CONFIG[iv.status]?.label}
                                </span>
                              </div>
                              {iv.position && <p className="text-sm text-muted-foreground">{iv.position}</p>}
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{iv.scheduled_date} at {iv.scheduled_time}</span>
                                <span>{iv.duration_minutes} min</span>
                                <span>{typeConf.label}</span>
                              </div>
                              {iv.candidate_email && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Mail className="w-3 h-3" />{iv.candidate_email}</p>}
                              {iv.notes && <p className="text-xs text-muted-foreground mt-1 italic">{iv.notes}</p>}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-border text-foreground"
                              onClick={() => { setShowStatus(iv); setStatusForm({ status: 'completed', feedback: '' }); }}
                              data-testid={`interview-update-${i}`}
                            >
                              Update
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Past */}
            {past.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-foreground mb-3">Past ({past.length})</h2>
                <div className="space-y-2">
                  {past.map((iv, i) => (
                    <Card key={i} className="glass-card border-border opacity-70" data-testid={`interview-past-${i}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-foreground font-medium text-sm">{iv.candidate_name}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[iv.status]?.color}`}>
                              {STATUS_CONFIG[iv.status]?.label}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{iv.position} - {iv.scheduled_date}</p>
                          {iv.feedback && <p className="text-xs text-muted-foreground mt-1">Feedback: {iv.feedback}</p>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Update Status Dialog */}
        <Dialog open={!!showStatus} onOpenChange={() => setShowStatus(null)}>
          <DialogContent className="sm:max-w-md glass-card border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground">Update Interview Status</DialogTitle>
            </DialogHeader>
            {showStatus && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Candidate: <span className="text-foreground font-medium">{showStatus.candidate_name}</span></p>
                <div>
                  <Label className="text-foreground">Status</Label>
                  <Select value={statusForm.status} onValueChange={v => setStatusForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="interview-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="no_show">No Show</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-foreground">Feedback</Label>
                  <textarea value={statusForm.feedback} onChange={e => setStatusForm(f => ({ ...f, feedback: e.target.value }))} className="mt-1 w-full rounded-md bg-muted border border-border text-foreground p-3 text-sm min-h-[80px] resize-none" placeholder="Interview feedback..." data-testid="interview-feedback-input" />
                </div>
                <Button className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={() => updateStatus(showStatus._id || showStatus.id)} data-testid="update-interview-status-btn">
                  Update Status
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
